import { Dropdown, message } from "antd";
import { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import Table from "@components/Table";
import { getColumns } from "./config";
import AddBenefitsPlan from "./AddBenefitsPlan";
import {
  useFetchBenefitsPlansQuery,
  useDeleteBenefitsPlanMutation,
  ErrorServices,
  benefitsPlanApi,
} from "@services";
import { ICashAllowance } from "@types";
import BenefitsPlanVersions from "./Versions";
import Upload from "@components/Upload";
import { useTypedSelector } from "@hooks";
import { checkPermission } from "@/utils";
import { permissions } from "@/router";
import CustomButton from "@/components/customButton";
import SearchBox from "@/components/searchInput";
import EditBenefitModal from "./EditBenefitModal";
import { formatNumberWithCommas } from "@/utils/formatNumberWithCommas";
import { DownOutlined } from "@ant-design/icons";

const BenefitsPlan = () => {
  const { company } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;
  const dispatch = useDispatch();
  const cashAllowance_id = useRef<any>(null);
  const [page, setPage] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const [isVisibleForEdit, setIsVisibleForEdit] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [order, setOrder] = useState<"asc" | "desc" | null>(null);

  const [isVersionsModal, setIsVersionsModal] = useState(false);
  const [selectedBenefitPlan, setSelectedBenefitPlan] =
    useState<null | ICashAllowance>(null);
  const { data: benefitPlanData, isLoading } = useFetchBenefitsPlansQuery({
    company_id,
    page,
    page_size: pageSize,
    sort_by: sortBy,
    order: order,
  });
  const [deleteBenefitsPlan, { isLoading: isDeleting }] =
    useDeleteBenefitsPlanMutation();
  const { data, pagination } = benefitPlanData || {};
  const removeBenefitsPlan = async (id: number) => {
    try {
      cashAllowance_id.current = id;
      await deleteBenefitsPlan({ company_id, id }).unwrap();
      message.success("Benefit Plan deleted successfully!");
    } catch (error) {
      cashAllowance_id.current = null;
      ErrorServices(error);
    }
  };

  const editBenefitsPlan = (cashAllowance: ICashAllowance) => {
    setSelectedBenefitPlan(cashAllowance);
    setIsVisibleForEdit(true);
  };

  const download = (id?: number) => {
    dispatch(
      benefitsPlanApi.endpoints.downloadBenefitsPlans.initiate(
        {
          company_id,
          version_id: id || null,
        },
        {
          subscribe: false,
          forceRefetch: true,
        }
      )
    );
  };

  const columns = getColumns({
    removeBenefitsPlan,
    isDeleting,
    cashAllowance_id,
    editBenefitsPlan,
  });

  const updatedData = data?.map((item: any) => ({
    ...item,
    value: item?.is_percentage
      ? item?.value
      : formatNumberWithCommas(item?.value),
  }));

  // ✅ COLUMN STATE
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(
    columns.map(col => col.key)
  );

  // ✅ FILTERED COLUMNS
  const filteredColumns = columns.filter(col =>
    visibleColumnKeys.includes(col.key)
  );

  // custom overlay
  const columnOverlay = (
    <div
      style={{
        background: "#fff",
        padding: 8,
        borderRadius: 6,
        boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
        minWidth: 240,
        maxHeight: 300,
        overflowY: "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {columns.map((col) => {
        const isActionsColumn = col.key === "actions";
        const isChecked = visibleColumnKeys.includes(col.key);
        const isDisabled =
          isActionsColumn || (isChecked && visibleColumnKeys.length <= 3);

        return (
          <label
            key={col.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "6px 8px",
              borderRadius: 4,
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled ? 0.6 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={isChecked}
              disabled={isDisabled}
              onChange={() => {
                if (isActionsColumn) return;

                if (isChecked) {
                  setVisibleColumnKeys((prev) =>
                    prev.filter((key) => key !== col.key)
                  );
                } else {
                  setVisibleColumnKeys((prev) => [...prev, col.key]);
                }
              }}
              style={{
                margin: 0,
                width: 16,
                height: 16,
                flexShrink: 0,
              }}
            />

            <span
              style={{
                flex: 1,
                fontSize: 14,
                lineHeight: "18px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {typeof col.title === "string"
                ? col.title.replace(/\b\w/g, c => c.toUpperCase())
                : col.title}
            </span>
          </label>
        );
      })}
    </div>
  );


  return (
    <>
      {isVisible && (
        <AddBenefitsPlan isVisible={isVisible} setIsVisible={setIsVisible} />
      )}
      {isVisibleForEdit && selectedBenefitPlan?.id && (
        <EditBenefitModal
          selectedBenefitPlan={selectedBenefitPlan}
          setSelectedBenefitPlan={setSelectedBenefitPlan}
          isVisible={isVisibleForEdit}
          setIsVisible={setIsVisibleForEdit}
        />
      )}
      {isVersionsModal && (
        <BenefitsPlanVersions
          isVisible={isVersionsModal}
          setIsVisible={setIsVersionsModal}
          download={download}
        />
      )}

      <div className="bg-themeCore ">
        <div className="jobCreationBar bg-white px-4 py-6 rounded-lg flex justify-between items-center ">
          <div className="title text-themeBlue  font-medium text-3xl">
            Benefit Plans
          </div>

          <div className="flex flex-row justify-between">
            {checkPermission(permissions.CREATE_BENEFITS_PLAN) && (
              <CustomButton
                onClick={() => setIsVisible(true)}
                btnName="Create Benefit Plan"
                customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
                iconPosition="left"
              ></CustomButton>
            )}
          </div>
        </div>
      </div>
      <div className="jobCreationListing mt-10 bg-white px-14 py-6 rounded-lg">
        <div className="flex items-center justify-end gap-2">
          <SearchBox
            placeholder="Type to search..."
            value={searchInput}
            onChange={value => setSearchInput(value)}
          />
          <Dropdown
            overlay={columnOverlay}
            trigger={["click"]}
            placement="bottomLeft"
          // getPopupContainer={trigger => trigger.parentElement}
          >
            <CustomButton
              btnName="Columns"
              customClasses="shadow-md font-medium border py-1 px-4"
            >
              <DownOutlined style={{ marginLeft: 6 }} />
            </CustomButton>
          </Dropdown>
        </div>
        <Table
          data={updatedData?.filter((item: any) => item?.id)}
          columns={filteredColumns}
          isLoading={isLoading}
          pagination={true}
          count={pagination?.count}
          onChangePage={setPage}
          onPageSizeChange={setPageSize}
          page={page}
          onTableChange={(pagination, filters, sorter) => {
            if (sorter?.field) {
              setSortBy(sorter.field);
              setOrder(sorter.order === "ascend" ? "asc" : "desc");
              setPage(1);
            }
          }}
        />
      </div>
    </>
  );
};
export default BenefitsPlan;
